import { CalendarPickerView, LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { FormControl, FormHelperText, IconButton, InputAdornment, TextField, TextFieldProps, Tooltip } from '@mui/material'
import { UseControllerProps, useController } from 'react-hook-form'
import { CustomDatePickerActionBar } from './Helper';
import { formControlColour, tooltipColour } from '../BEE123.Constants';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import React, { SetStateAction } from 'react';
import { getDateWithoutTime } from '../../../../Helpers/DateFormatter';
import { ValidatedMobileDatePicker } from '../ValidatedMobileDatePicker/ValidatedMobileDatePicker';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';

interface IProps extends UseControllerProps {
    label: string;
    inputFormat: string;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    onChange?: (date: Date) => void;
    showDescription?: boolean;
    descriptionText?: string;
    showCustomError?: boolean;
    customText?: string;
    showError?: boolean;
    showCalendarIcon?: boolean;
    views?: CalendarPickerView[];
}

export const AppDatepicker = (props: IProps) => {
    const { fieldState, field } = useController({ ...props, defaultValue: props.defaultValue != null ? props.defaultValue : '' });
    const [currentView, setCurrentView] = React.useState<CalendarPickerView>(
        props.views?.[0] ?? 'day'
    );
    
    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <FormControl fullWidth error={!!fieldState.error}>
                <ValidatedMobileDatePicker
                    inputFormat={props.inputFormat}
                    label={props.label}
                    value={field.value || null}
                    mask="____/__/__"
                    onChange={(value: Date) => {
                        if (value instanceof Date && !isNaN(value.getTime())) {
                            const date = getDateWithoutTime(value);
                            field.onChange(date);
                            if (props.onChange) {
                                props.onChange(new Date(date.getTime()));
                            }
                        }
                    }}
                    components={{ 
                        ActionBar: (actionProps: PickersActionBarProps) => ( 
                            <CustomDatePickerActionBar 
                                {...actionProps} 
                                onClear={() => { 
                                    field.onChange(null); 
                                    actionProps.onCancel(); 
                                }} 
                            /> 
                        ) 
                    }}
                    renderInput={(params: TextFieldProps) =>
                        <TextField
                            {...params}
                            error={!!fieldState.error || props.showError}
                            InputProps={{
                                endAdornment: props.showCalendarIcon ? (
                                    <InputAdornment position="end">
                                        <CalendarMonthIcon
                                            fontSize="small"
                                            sx={{
                                                color: formControlColour,
                                            }}
                                        />
                                    </InputAdornment>
                                ) : null,
                            }}
                        />}
                    disabled={props.disabled}
                    minDate={props.minDate}
                    maxDate={props.maxDate}
                    views={props.views ? props.views : ['year', 'month', 'day']}
                    onViewChange={(view: SetStateAction<CalendarPickerView>) => setCurrentView(view)}
                    onError={(error: any, value: any) => {
                        if (currentView === 'year') {
                            const year = value?.getFullYear?.();
                            if (!year || year.toString().length < 4) {
                                field.onChange(error);
                            }
                        }
                    }} 
                />
                {props.showDescription &&
                    <Tooltip
                        title={props.descriptionText!}
                        arrow
                        componentsProps={{
                            tooltip: {
                                sx: {
                                    bgcolor: tooltipColour,
                                    '& .MuiTooltip-arrow': {
                                        color: tooltipColour
                                    },
                                },
                            },
                        }}
                        placement="right"
                    >
                        <IconButton
                            size="small"
                            edge="end"
                            color="inherit"
                            aria-label="info"
                            sx={{ position: 'absolute', right: '0', top: '50%', transform: 'translateY(-50%)', pr: '1rem' }}
                        >
                            <InfoOutlinedIcon style={{ color: formControlColour }} />
                        </IconButton>
                    </Tooltip>
                }

                <FormHelperText>{fieldState.error?.message}</FormHelperText>
            </FormControl>
        </LocalizationProvider>


    )
}

